import { Activity } from 'app/shared/features/activity-selector/models';

import { ScannedCode, ScannedCodeType, ScannedCodeWithEntity } from '..//models';

/*
 * This utility file handles operations on a given list of scanned codes
 * It differs from scanned-code.utils which handles single scanned code
 */

interface IHasCodeType {
    codeType: ScannedCodeType;
}

export function sortScannedCodes(codes: ScannedCode[]) {
    return [...codes].sort((a, b) => a.code.localeCompare(b.code));
}

// -- Get Scanned Entity code based on given Scanned Code List --
export function filterByCodeType<T extends IHasCodeType>(list: T[], codeType: ScannedCodeType) {
    return list
        .filter((scannedCode) => scannedCode !== null)
        .filter((scannedCode) => scannedCode.codeType === codeType);
}

export function getFirstByCodeType<T extends IHasCodeType>(list: T[], codeType: ScannedCodeType) {
    return filterByCodeType(list, codeType).find((i) => !!i);
}

export function getActivityCode(scannedCodes: ScannedCode[]): ScannedCodeWithEntity<Activity> | undefined {
    const scannedCode = getFirstByCodeType(scannedCodes, 'activity');

    return scannedCode ? (scannedCode as ScannedCodeWithEntity<Activity>) : undefined;
}

export function getBatchCode(scannedCodes: ScannedCode[]): ScannedCode | undefined {
    const scannedCode = getFirstByCodeType(scannedCodes, 'batch');

    return scannedCode ? (scannedCode as ScannedCode) : undefined;
}

// -- Get Scanned Entity Number based on given Scanned Code List --
export const getScannedCodeNumbersByCodeType = (scannedCodes: ScannedCode[], codeType: ScannedCodeType) =>
    scannedCodes
        .filter((scannedCode) => scannedCode && scannedCode.codeType === codeType)
        .map((code) => code.number)
        .filter((number): number is number => !!number);

export const getActivityNumberFromScannedCodes = (scannedCodes: ScannedCode[]) => {
    const activityNumbers = getScannedCodeNumbersByCodeType(scannedCodes, 'activity');
    return activityNumbers.length ? activityNumbers[0] : undefined;
};

export const getBatchNumberFromScannedCodes = (scannedCodes: ScannedCode[]) => {
    const batchNumber = getScannedCodeNumbersByCodeType(scannedCodes, 'batch');
    return batchNumber.length ? batchNumber[0] : undefined;
};

export const getLocationNumbersFromScannedCodes = (scannedCodes: ScannedCode[]) => {
    return getScannedCodeNumbersByCodeType(scannedCodes, 'location');
};
